import { Address, RecurlyError } from "@recurly/recurly-js";
import { FieldMetaState } from "react-final-form";

import { IBilling } from "store/entities/billing";
import { prettyJoin } from "utils/string";

export type ICardFormValues = IRecurlyBillingData & {
  creditCardError?: string | null;
};

export type CardFormSubmissionErrors = Partial<ICardFormValues>;

export type IRecurlyBillingData = Pick<
  Address,
  "first_name" | "last_name" | "vat_number" | "address1" | "city" | "state"
> & {
  country: string;
  postal_code: string;
  company?: string;
};

export type IRecurlyBillingFields = keyof IRecurlyBillingData;

export type IRecurlyCardApiErrorFields = IRecurlyBillingFields | "card" | "number" | "cvv" | "month" | "year";

export const requiredCardFormFieldsErrors: Partial<Record<keyof ICardFormValues, string>> = {
  first_name: "Enter your first name",
  last_name: "Enter your last name",
  creditCardError: "Enter credit card details",
  country: "Select country",
  postal_code: "Enter ZIP/postal code",
};

export const recurlyApiErrorFormFieldNames: Record<IRecurlyCardApiErrorFields, string> = {
  card: "Credit card",
  number: "Number",
  cvv: "CVV",
  month: "Month",
  year: "Year",
  first_name: "First name",
  last_name: "Last name",
  postal_code: "Postal code",
  company: "Company name",
  vat_number: "VAT ID",
  address1: "Address",
  city: "City",
  state: "State",
  country: "Country",
};

export const generateCardFormSubmissionErrors = (error: RecurlyError): CardFormSubmissionErrors => {
  const errors: CardFormSubmissionErrors = {};
  const cardFieldErrors: string[] = [];

  error.details?.forEach(errorDetail => {
    const { field, messages } = errorDetail;
    const fieldName = recurlyApiErrorFormFieldNames[field as IRecurlyCardApiErrorFields];

    if (["number", "month", "year", "cvv"].includes(field)) {
      cardFieldErrors.push(`${fieldName} ${prettyJoin(messages)}`);
      return;
    }

    errors[field as keyof CardFormSubmissionErrors] = `${fieldName} ${prettyJoin(messages)}`;
  });

  if (cardFieldErrors.length) {
    errors.creditCardError = cardFieldErrors.join(". ");
  }

  return errors;
};

export const getCardFormFieldError = ({
  touched,
  error,
  submitError,
  dirtySinceLastSubmit,
}: FieldMetaState<unknown>): string | undefined => {
  return touched && (error || (!dirtySinceLastSubmit && submitError));
};

export const hasCardFormFieldError = (meta: FieldMetaState<unknown>): boolean => Boolean(getCardFormFieldError(meta));

export const generateCreditCardFormInitialValues = (billing: IBilling | null): Partial<ICardFormValues> => {
  let initialValues: Partial<ICardFormValues> = {
    creditCardError: requiredCardFormFieldsErrors.creditCardError,
  };

  if (billing?.billingInfo) {
    const { firstName, lastName, country, zip, company, vatNumber, address, city, region } = billing.billingInfo;

    initialValues = {
      ...initialValues,
      first_name: firstName,
      last_name: lastName,
      country,
      postal_code: zip,
      company,
      vat_number: vatNumber,
      address1: address,
      city,
      state: region,
    };
  }

  return initialValues;
};
